<template>
    <nav
        v-if="!cmsView || isSalesperson"
        class="relative z-10 w-full bg-white shadow-sm border-b border-gray-200"
        aria-label="Primary"
    >
        <!-- Desktop: category rail -->
        <div class="container mx-auto hidden md:flex items-center gap-2">
            <!-- Home -->
            <button
                class="px-4 py-3 text-sm font-medium text-gray-700 hover:text-green-700 focus:outline-none focus-visible:ring"
                @click="$emit('gotoHome')"
            >
                Home
            </button>
            <div class="relative">
                <!-- Categories -->
                <div
                    v-for="(parent, idx) in categoriesGroupedByParent"
                    :key="'parent-' + parent.id"
                    class="inline-block"
                    @mouseenter="openWithDelay(idx)"
                    @mouseleave="closeWithDelay"
                >
                    <button
                        :aria-expanded="openIndex === idx ? 'true' : 'false'"
                        :aria-controls="'megapanel-' + parent.id"
                        class="px-4 py-3 text-sm font-medium text-gray-800 hover:text-green-700 focus:outline-none focus-visible:ring flex items-center gap-2"
                        @focus="openIndex = idx"
                        @keydown.down.prevent="openIndex = idx"
                        @click="
                            !parent.children || parent.children.length === 0
                                ? (emitFilter(parent.id), (mobileOpen = false))
                                : (openIndex = idx)
                        "
                    >
                        <span class="truncate">{{ parent.name }}</span>
                        <svg
                            v-if="parent.children.length > 0"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>

                    <!-- Mega panel -->
                    <transition
                        v-if="parent.children.length > 0"
                        name="fade-slide"
                    >
                        <div
                            v-if="openIndex === idx"
                            :id="'megapanel-' + parent.id"
                            class="absolute left-0 top-full w-[72rem] max-w-[90vw] bg-white shadow-xl border border-gray-200 rounded-b-xl p-4"
                            role="region"
                        >
                            <div class="grid grid-cols-12 gap-4">
                                <!-- Links area -->
                                <div
                                    class="col-span-12 lg:col-span-9 grid grid-cols-2 md:grid-cols-3 gap-4 max-h-80 overflow-y-auto pr-2"
                                >
                                    <div
                                        v-for="(col, cIdx) in makeColumns(parent.children)"
                                        :key="'col-' + cIdx"
                                        class="space-y-2"
                                    >
                                        <div
                                            v-for="child in col"
                                            :key="'child-' + child.id"
                                            class=""
                                        >
                                            <div
                                                class="text-sm font-semibold text-gray-900 hover:text-green-700 cursor-pointer"
                                                @click="emitFilter(child.id)"
                                            >
                                                {{ child.name }}
                                            </div>

                                            <!-- Grand-children as pills -->
                                            <div class="mt-1 flex flex-wrap gap-1.5">
                                                <button
                                                    v-for="grand in child.children || []"
                                                    :key="'grand-' + grand.id"
                                                    class="text-xs px-2 py-1 rounded-full border border-gray-200 hover:border-green-600 hover:text-green-700 text-gray-600 bg-gray-50"
                                                    @click="emitFilter(grand.id)"
                                                >
                                                    {{ grand.name }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Promo/banner area -->
                                <div class="col-span-12 lg:col-span-3">
                                    <div class="h-80 w-full overflow-hidden rounded-xl bg-gray-100">
                                        <img
                                            v-if="parent.banner_path"
                                            :src="parent.banner_path"
                                            alt="Category banner"
                                            class="w-full h-full object-cover"
                                        />
                                        <div
                                            v-else
                                            class="w-full h-full flex items-center justify-center text-gray-400 text-sm"
                                        >
                                            Add a banner for {{ parent.name }}
                                        </div>
                                    </div>
                                    <div
                                        class="mt-3 grid grid-cols-2 gap-2"
                                        v-if="(parent.featured || []).length"
                                    >
                                        <button
                                            v-for="feat in parent.featured"
                                            :key="'feat-' + feat.id"
                                            class="text-xs px-2 py-2 rounded-lg border border-gray-200 hover:border-green-600 hover:text-green-700 text-gray-700 bg-white text-left truncate"
                                            @click="emitFilter(feat.id)"
                                        >
                                            🔥 {{ feat.name }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>

            <!-- Wholesalers entry (optional slot) -->
            <slot name="extra"></slot>
        </div>

        <!-- Mobile: hamburger + drawer -->
        <div class="md:hidden">
            <div class="container mx-auto flex items-center justify-between px-3">
                <button
                    class="px-2 py-3 text-sm font-medium text-gray-800 focus:outline-none"
                    @click="$emit('gotoHome')"
                >
                    Home
                </button>
                <button
                    class="p-3 focus:outline-none"
                    @click="mobileOpen = !mobileOpen"
                    :aria-expanded="mobileOpen ? 'true' : 'false'"
                    aria-controls="mobile-mega"
                >
                    <svg
                        class="w-6 h-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 6h16M4 12h16M4 18h16"
                        />
                    </svg>
                </button>
            </div>

            <transition name="fade-slide">
                <div
                    v-if="mobileOpen"
                    id="mobile-mega"
                    class="border-t border-gray-200"
                >
                    <div class="max-h-[70vh] overflow-y-auto">
                        <details
                            v-for="parent in categoriesGroupedByParent"
                            :key="'m-parent-' + parent.id"
                            class="group"
                        >
                            <summary
                                @click="
                                    !parent.children || parent.children.length === 0
                                        ? (emitFilter(parent.id), (mobileOpen = false))
                                        : (openIndex = idx)
                                "
                                class="px-4 py-3 text-sm font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
                            >
                                <span>{{ parent.name }}</span>
                                <svg
                                    v-if="parent.children.length > 0"
                                    class="w-4 h-4 transition-transform group-open:rotate-180"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </summary>
                            <div
                                v-if="parent.children.length > 0"
                                class="px-4 pb-3 space-y-2"
                            >
                                <div
                                    v-for="child in parent.children"
                                    :key="'m-child-' + child.id"
                                >
                                    <div
                                        class="text-sm font-medium text-gray-800 py-1"
                                        @click="
                                            emitFilter(child.id);
                                            mobileOpen = false;
                                        "
                                    >
                                        {{ child.name }}
                                    </div>
                                    <div class="mt-1 flex flex-wrap gap-1.5">
                                        <button
                                            v-for="grand in child.children || []"
                                            :key="'m-grand-' + grand.id"
                                            class="text-xs px-2 py-1 rounded-full border border-gray-200 text-gray-600 bg-gray-50"
                                            @click="
                                                emitFilter(grand.id);
                                                mobileOpen = false;
                                            "
                                        >
                                            {{ grand.name }}
                                        </button>
                                    </div>
                                </div>
                                <!-- No need to show banner in mobile view -->
                                <!-- <div
                                    class="mt-2"
                                    v-if="parent.banner_path"
                                >
                                    <img
                                        :src="parent.banner_path"
                                        alt="Banner"
                                        class="w-full h-36 object-cover rounded-lg"
                                    />
                                </div> -->
                            </div>
                        </details>
                    </div>
                </div>
            </transition>
        </div>
    </nav>
</template>

<script>
    export default {
        name: 'CategoryMegaMenu',
        props: {
            status: {
                type: Boolean,
                default: false,
            },
        },
        // props: {
        //     categories: {
        //         type: Array,
        //         default: () => [],
        /*
        Expected shape (example):
        [
          {
            id: 1,
            name: 'Electronics',
            banner_path: '/storage/banners/electronics.jpg',
            featured: [{ id: 99, name: 'Top Deals' }], // optional
            children: [
              { id: 11, name: 'Mobiles', children: [ { id: 111, name: '5G Phones' } ] },
              { id: 12, name: 'Laptops', children: [ { id: 121, name: 'Gaming' } ] },
              ...
            ]
          },
          ...
        ]
      */
        //     },
        // },
        // data() {
        //     return {
        //         openIndex: null,
        //         hoverTimer: null,
        //         mobileOpen: false,
        //     };
        // },

        data() {
            return {
                categoriesGroupedByParent: [],
                wholesalers: [],
                wholesalerSearchKeyword: null,

                showCategories: false,

                openIndex: null,
                hoverTimer: null,
                mobileOpen: false,
            };
        },

        computed: {
            filteredWholesalers() {
                return this.wholesalers.filter(
                    (wholesaler) =>
                        !this.wholesalerSearchKeyword ||
                        wholesaler.name.toLowerCase().includes(this.wholesalerSearchKeyword.toLowerCase()),
                );
            },
        },

        async beforeMount() {
            await this.fetchCategoriesGroupedByParent();
            // await this.fetchWholesalers();
        },

        methods: {
            async fetchCategoriesGroupedByParent() {
                await this.$axios.get(route('categories.groupByParent')).then((res) => {
                    this.categoriesGroupedByParent = res.data.categoriesGroupedByParent;
                });
            },

            async fetchWholesalers() {
                await this.$axios.get(route('wholesalers.index')).then((res) => {
                    this.wholesalers = res.data.wholesalers;
                });
            },

            gotoProductsIndex() {
                this.$inertia.get(route('products.index'));
            },

            emitFilter(id) {
                this.$emit('filterByCategory', id);
            },

            makeColumns(children) {
                // Flipkart-style dense columns: aim for up to ~7 parent rows per col
                const list = Array.isArray(children) ? children : [];
                const maxPerCol = 7; // tweak to taste
                const cols = [];
                for (let i = 0; i < list.length; i += maxPerCol) {
                    cols.push(list.slice(i, i + maxPerCol));
                }
                // ensure at least 2 columns for balance when items are few
                if (cols.length === 1) {
                    cols.push([]);
                }
                return cols;
            },

            openWithDelay(idx) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = setTimeout(() => {
                    this.openIndex = idx;
                }, 60); // quick, snappy
            },

            closeWithDelay() {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = setTimeout(() => {
                    this.openIndex = null;
                }, 120);
            },
        },
    };
</script>

<style scoped>
    .container {
        @apply px-3;
    }
    .fade-slide-enter-active,
    .fade-slide-leave-active {
        transition: all 0.15s ease;
    }
    .fade-slide-enter,
    .fade-slide-leave-to {
        opacity: 0;
        transform: translateY(6px);
    }
    /* prevent pointer gaps closing the panel */
    nav > .container > div.relative {
        position: relative;
    }
</style>
